import { type EnvironmentProviders, makeEnvironmentProviders, type Provider } from '@angular/core';

import { of } from 'rxjs';

import { APP_CONFIG, AUTH_METHOD, AUTH_TOKEN } from '@core/common-legacy';
import {
  provideTellentAssistantActorGetter,
  provideTellentAssistantAppContextGetter,
  provideTellentAssistantAuthTokenGetter,
  provideTellentAssistantConfig,
  provideTellentAssistantResourceIdGetter,
  provideTellentConversationStartersContext,
  type TellentAssistantConfig,
} from '@core/tellent-assistant-contracts';
import {
  provideConversationStartersNgrx,
  provideTellentAssistantCurrentThreadNgrx,
  provideTellentAssistantEngagementsNgrx,
  provideTellentAssistantSuggestionsNgrx,
  provideTellentAssistantThreadsNgrx,
} from '@core/tellent-assistant-logic';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { ORG_GUID } from '@core/tellent-types';

import { providePresentChartClientTool } from './client-tools/present-chart.provider';
import {
  providePresentCandidatesClientTool,
  providePresentEvaluationsClientTool,
  providePresentEventsClientTool,
  providePresentJobsClientTool,
  providePresentLinksClientTool,
  providePresentRequisitionsClientTool,
  providePresentSourcesClientTool,
  providePresentTalentPoolsClientTool,
  providePresentTasksClientTool,
  providePresentTeamMembersClientTool,
} from './client-tools/present-list.provider';
import { providePresentTableDataClientTool } from './client-tools/present-table-data.provider';
import { provideTellentAssistant } from './tellent-assistant.providers';

interface TellentAssistantHostAppConfig {
  readonly tellentApiUrl: string;
}

export function provideTellentAssistantHost(): EnvironmentProviders {
  return makeEnvironmentProviders([
    ...buildTellentAssistantHostNonNgrxProviders(),
    ...provideTellentAssistantThreadsNgrx,
    ...provideTellentAssistantCurrentThreadNgrx,
    ...provideConversationStartersNgrx,
    ...provideTellentAssistantSuggestionsNgrx,
    ...provideTellentAssistantEngagementsNgrx,
  ]);
}

/**
 * Non-NgRx providers shared by the standalone helper and the legacy NgModule
 * bridge (`TellentAssistantHostModule`). NgRx registration differs between
 * the two: standalone uses `provideState`, legacy uses `StoreModule.forFeature`.
 */
export function buildTellentAssistantHostNonNgrxProviders(): Array<
  Provider | EnvironmentProviders
> {
  return [
    provideTellentAssistant({
      config: { apiUrl: '', devPlayground: false },
      authTokenGetter: () => null,
    }),
    provideTellentAssistantConfig({
      useFactory: resolveConfig,
      deps: [APP_CONFIG, ORG_GUID],
    }),
    provideTellentAssistantAuthTokenGetter({
      useFactory: resolveAuthTokenGetter,
      deps: [AUTH_TOKEN, AUTH_METHOD],
    }),
    provideTellentAssistantResourceIdGetter({
      useFactory: resolveResourceIdGetter,
      deps: [TellentCurrentUserDataFacade],
    }),
    provideTellentAssistantActorGetter({
      useFactory: resolveActorGetter,
      deps: [ORG_GUID, TellentCurrentUserDataFacade],
    }),
    provideTellentAssistantAppContextGetter({ useValue: () => ({}) }),
    provideTellentConversationStartersContext({ useValue: of(null) }),
    providePresentTableDataClientTool,
    providePresentChartClientTool,
    providePresentCandidatesClientTool,
    providePresentTeamMembersClientTool,
    providePresentJobsClientTool,
    providePresentTalentPoolsClientTool,
    providePresentLinksClientTool,
    providePresentSourcesClientTool,
    providePresentEventsClientTool,
    providePresentEvaluationsClientTool,
    providePresentTasksClientTool,
    providePresentRequisitionsClientTool,
  ];
}

function resolveConfig(
  appConfig: TellentAssistantHostAppConfig,
  orgGuid: string | null,
): TellentAssistantConfig {
  // For local development only
  // return { apiUrl: 'http://127.0.0.1:4111', devPlayground: true };

  return {
    apiUrl: `${appConfig.tellentApiUrl}/intelligence/org/${orgGuid}/assistant/proxy`,
    devPlayground: false,
  };
}

function resolveAuthTokenGetter(token: string, method: string): () => string | null {
  return () => (token ? `${method} ${token}` : null);
}

function resolveResourceIdGetter(
  currentUserFacade: TellentCurrentUserDataFacade,
): () => string | null {
  return () => currentUserFacade.currentUserGuid() ?? null;
}

function resolveActorGetter(
  orgGuid: string | null,
  currentUserFacade: TellentCurrentUserDataFacade,
): () => string | null {
  return () => {
    const user = currentUserFacade.currentUserGuid();
    const member = currentUserFacade.organizationMembership()?.guid;
    if (!orgGuid || !user || !member) return null;
    return JSON.stringify({
      type: 'account',
      data: { org: orgGuid, user, member },
      features: ['ask', 'wiki', 'hr_assistant'],
    });
  };
}
